import { faHome } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Breadcrumb,
  Button,
  Col,
  Form,
  Row,
} from '@themesberg/react-bootstrap';
import Joi from 'joi';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { updateToastInfo } from '../../actions/settings';
import { updateSpinnerState } from '../../actions/spinner';
import { returnValidationErrorMessages } from '../../utils/validation';
import { isValidGSTNumber } from '../../utils/vendors';
import { addNewVehicleVendor } from '../../parse-functions/vendors';
import EditableDropDown from '../../components/common/EditableDropdown';
import { arrayToDropdownOptions } from '../../utils/sms/arrayToDropdownOptions';
import { ErrorMessage } from '../scheme-management/Components/StyledComponents';

const addVendorSchema = Joi.object({
  vendorName: Joi.string().required(),
  vendorCode: Joi.string().allow(''),
  emailId: Joi.string()
    .allow('')
    .email({ tlds: { allow: false } }),
  contactNumber: Joi.number().required().greater(1000000000).less(9999999999),
  gstNumber: Joi.string()
    .required()
    .custom(isValidGSTNumber, 'Invalid GST Number Format'),
  branch: Joi.string().required(),
  zone: Joi.string().required(),
  // panNumber: Joi.string().required(),
  // accountNumber: Joi.string().required(),
  // bankName: Joi.string().required(),
  // ifscCode: Joi.string().required(),
});
const AddVendor = ({ editData, handleSave }) => {
  const warehouseZone = useSelector((state) => state.user.warehouse.zone || '');
  const initialFormInput = editData || {
    vendorName: '',
    vendorCode: '',
    emailId: '',
    contactNumber: '',
    gstNumber: '',
    zone: warehouseZone,
    panNumber: '',
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    accountHolder: '',
    branch: '',
  };
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const configData = useSelector((state) => state.user.config || {});
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState(initialFormInput);
  const isAdmin = useSelector(
    (state) => state?.user?.preferences?.isAdmin || false
  );
  const warehouse = useSelector((state) => state.user.warehouse || {});
  const otherWarehouses = useSelector(
    (state) => state?.user?.otherWarehousesInZone || {}
  );

  const onFormInputChange = (fieldName, value) => {
    setFormData((oldFormData) => ({
      ...oldFormData,
      [fieldName]: value,
    }));
  };
  const handleFormSubmit = async () => {
    const { error: formErrors = {}, value: formValue } =
      addVendorSchema.validate(formData, {
        abortEarly: false,
        allowUnknown: true,
      });

    const { details = {} } = formErrors;
    if (details?.length > 0) {
      const validationErrorMessages = returnValidationErrorMessages(details);
      setFormErrors(validationErrorMessages);
    } else {
      dispatch(updateSpinnerState(true));
      try {
        const savedVendor = await addNewVehicleVendor(formValue);
        setFormData(initialFormInput);
        dispatch(
          updateToastInfo({
            show: true,
            type: 'success',
            title: t('Vendor Added'),
            message: t(`${formData.vendorName} added successfully`),
          })
        );
      } catch (error) {
        // inside error
        const { message, code } = error;
        setFormErrors(message);
        dispatch(
          updateToastInfo({
            show: true,
            type: 'danger',
            title: t('Vendor Add Failed'),
            message: error.message,
          })
        );
      }
      dispatch(updateSpinnerState(false));
    }
  };
  return (
    <div className="mt-1">
      {!editData && (
        <div className="mb-4 d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-2">
          <div className="d-block mb-4 mb-md-0">
            <Breadcrumb
              className="d-none d-md-inline-block"
              listProps={{
                className: 'breadcrumb-dark breadcrumb-transparent',
              }}
            >
              <Breadcrumb.Item>
                <FontAwesomeIcon icon={faHome} />
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t('Vendor')}</Breadcrumb.Item>
              <Breadcrumb.Item active>{t('Add')}</Breadcrumb.Item>
            </Breadcrumb>
            <h4>{t('Add New Vendor')}</h4>
            <p className="mb-0">
              {t('Use this page to add a new vehicle vendor')}
            </p>
          </div>
        </div>
      )}
      {/* <Alert variant="info">
        <FontAwesomeIcon icon={faHome} />
        &nbsp;&nbsp;{t('Vendor Info')}
      </Alert> */}
      <Row className="mt-3">
        <Col>
          <Form.Group className="mb-3 mt-4">
            <Form.Label>{t('Vendor Name')}</Form.Label>
            <Form.Control
              required
              isInvalid={formErrors['vendorName']?.length}
              type="text"
              onChange={(event) => {
                onFormInputChange('vendorName', event.target.value);
              }}
              value={formData.vendorName}
              disabled={editData}
            />
            {formErrors['vendorName']?.length && (
              <Form.Control.Feedback type="invalid">
                {t('Please enter Vendor name.')}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t('Vendor Code')}</Form.Label>
            <Form.Control
              isInvalid={formErrors['vendorCode']?.length}
              type="text"
              onChange={(event) => {
                onFormInputChange('vendorCode', event.target.value);
              }}
              value={formData.vendorCode}
            />
            {formErrors['vendorCode']?.length && (
              <Form.Control.Feedback type="invalid">
                {t('Please enter vendorCode.')}
              </Form.Control.Feedback>
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('Vendor Email')}</Form.Label>
            <Form.Control
              isInvalid={formErrors['emailId']?.length}
              type="email"
              onChange={(event) => {
                onFormInputChange('emailId', event.target.value);
              }}
              value={formData.emailId}
            />
            {formErrors['emailId']?.length && (
              <Form.Control.Feedback type="invalid">
                {t(formErrors['emailId'])}
              </Form.Control.Feedback>
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('Vendor Contact')}</Form.Label>
            <Form.Control
              isInvalid={formErrors['contactNumber']?.length}
              type="number"
              onChange={(event) => {
                onFormInputChange('contactNumber', event.target.value);
              }}
              value={formData.contactNumber}
              disabled={editData}
            />
            {formErrors['contactNumber']?.length && (
              <Form.Control.Feedback type="invalid">
                {t(formErrors['contactNumber'])}
              </Form.Control.Feedback>
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('GST Number')}</Form.Label>
            <Form.Control
              isInvalid={formErrors['gstNumber']?.length}
              type="string"
              onChange={(event) => {
                onFormInputChange('gstNumber', event.target.value);
              }}
              disabled={editData}
              value={formData.gstNumber}
            />
            {formErrors['gstNumber']?.length && (
              <Form.Control.Feedback type="invalid">
                {t(formErrors['gstNumber'])}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t('Zone')}</Form.Label>
            <EditableDropDown
              onChange={(event) => {
                onFormInputChange('zone', event.value);
              }}
              value={formData.zone}
              options={
                isAdmin
                  ? arrayToDropdownOptions(Object.keys(configData.zones))
                  : arrayToDropdownOptions([warehouseZone])
              }
              isInvalid={formErrors.zone}
            />

            {formErrors['zone']?.length && (
              <ErrorMessage>{t('Please choose a zone')}</ErrorMessage>
            )}
          </Form.Group>
        </Col>
        <Col>
          <Form.Group className="mb-3 mt-4">
            <Form.Label>{t('Account Holder')}</Form.Label>
            <Form.Control
              isInvalid={formErrors['accountHolder']?.length}
              type="string"
              onChange={(event) => {
                onFormInputChange('accountHolder', event.target.value);
              }}
              value={formData.accountHolder}
            />
            {formErrors['accountHolder']?.length && (
              <Form.Control.Feedback type="invalid">
                {t(formErrors['accountHolder'])}
              </Form.Control.Feedback>
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('PAN Number')}</Form.Label>
            <Form.Control
              isInvalid={formErrors['panNumber']?.length}
              type="string"
              onChange={(event) => {
                onFormInputChange('panNumber', event.target.value);
              }}
              value={formData.panNumber}
            />
            {formErrors['panNumber']?.length && (
              <Form.Control.Feedback type="invalid">
                {t(formErrors['panNumber'])}
              </Form.Control.Feedback>
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('Bank Name')}</Form.Label>
            <Form.Control
              isInvalid={formErrors['bankName']?.length}
              type="string"
              onChange={(event) => {
                onFormInputChange('bankName', event.target.value);
              }}
              value={formData.bankName}
            />
            {formErrors['bankName']?.length && (
              <Form.Control.Feedback type="invalid">
                {t(formErrors['bankName'])}
              </Form.Control.Feedback>
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('Account Number')}</Form.Label>
            <Form.Control
              isInvalid={formErrors['accountNumber']?.length}
              type="string"
              onChange={(event) => {
                onFormInputChange('accountNumber', event.target.value);
              }}
              value={formData.accountNumber}
            />
            {formErrors['accountNumber']?.length && (
              <Form.Control.Feedback type="invalid">
                {t(formErrors['accountNumber'])}
              </Form.Control.Feedback>
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('IFSC Code')}</Form.Label>
            <Form.Control
              isInvalid={formErrors['ifscCode']?.length}
              type="string"
              onChange={(event) => {
                onFormInputChange('ifscCode', event.target.value);
              }}
              value={formData.ifscCode}
            />
            {formErrors['ifscCode']?.length && (
              <Form.Control.Feedback type="invalid">
                {t(formErrors['ifscCode'])}
              </Form.Control.Feedback>
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('Branch')}</Form.Label>
            <EditableDropDown
              onChange={(event) => {
                onFormInputChange('branch', event.value);
              }}
              value={formData.branch}
              options={
                isAdmin
                  ? arrayToDropdownOptions(
                      Object.values(otherWarehouses).map(
                        (item) => item.warehouseName
                      )
                    )
                  : arrayToDropdownOptions([warehouse.warehouseName])
              }
              isInvalid={formErrors.branch}
            />
            {formErrors.branch?.length && (
              <ErrorMessage> {t(formErrors.branch)}</ErrorMessage>
            )}
          </Form.Group>

          <Button
            style={{ width: '98%' }}
            className="mx-2"
            variant="primary"
            onClick={() => {
              editData ? handleSave(formData) : handleFormSubmit();
            }}
          >
            {editData ? t('Edit Vendor') : t('Add Vendor')}
          </Button>
        </Col>
      </Row>
    </div>
  );
};
export default AddVendor;
